import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import ScoreRing from "@/components/ScoreRing";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, Building2, CheckCircle2, Copy, Loader2, Lock, MapPin, Sparkles, TrendingUp, Wand2, Lightbulb } from "lucide-react";

const STATUSES = ["À contacter", "Candidature envoyée", "Relance", "Entretien", "Accepté", "Refusé"];

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, upgrade } = useAuth();
  const [appData, setAppData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);
  const [editable, setEditable] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/applications/${id}`);
      setAppData(data);
      setEditable(data.assets || {});
    } catch {
      toast.error("Candidature introuvable.");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const generate = async () => {
    if (!user?.is_pro) {
      toast.info("Passe en Pro pour générer les documents.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post(`/applications/${id}/generate`);
      setAppData(data);
      setEditable(data.assets || {});
      toast.success("Documents générés !");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Génération impossible.");
    } finally {
      setGenerating(false);
    }
  };

  const doUpgrade = async () => {
    setUpgrading(true);
    setCheckoutUrl(null);
    setCheckoutError(null);

    let url;
    try {
      url = await upgrade();
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        (err?.code === "ECONNABORTED"
          ? "Le serveur n'a pas répondu en 5 secondes."
          : err?.message) ||
        "Erreur inconnue";
      const message = `Impossible d'ouvrir le paiement. Vérifiez votre configuration Stripe. (${detail})`;
      setCheckoutError(message);
      toast.error(message, { duration: 10000 });
      setUpgrading(false);
      return;
    }

    setCheckoutUrl(url);
    window.location.href = url;
    // Guarantee no infinite spinner: re-enable the button after 1.5s. If the
    // browser actually navigates, this component unmounts before it fires.
    setTimeout(() => setUpgrading(false), 1500);
  };

  const setStatus = async (status) => {
    setSavingStatus(true);
    try {
      const { data } = await api.patch(`/applications/${id}`, { status });
      setAppData(data);
      toast.success("Statut mis à jour.");
    } catch {
      toast.error("Mise à jour impossible.");
    } finally {
      setSavingStatus(false);
    }
  };

  const saveAsset = async (field) => {
    try {
      const { data } = await api.patch(`/applications/${id}`, { [field]: editable[field] });
      setAppData(data);
      toast.success("Enregistré.");
    } catch {
      toast.error("Enregistrement impossible.");
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text || "");
      toast.success("Copié !");
    } catch {
      toast.error("Copie impossible.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60">
        <Nav />
        <div className="max-w-6xl mx-auto px-4 py-16 text-center text-slate-400">Chargement…</div>
      </div>
    );
  }
  if (!appData) return null;

  const a = appData.analysis;
  const assets = appData.assets;
  const isPro = user?.is_pro;

  return (
    <div className="min-h-screen bg-slate-50/60">
      <Nav />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-500 -ml-3" data-testid="detail-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Retour aux candidatures
        </Button>

        {/* Header */}
        <div className="mt-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <Badge variant="outline" className="rounded-full text-slate-500 border-slate-200">{appData.contract_type}</Badge>
            <h1 className="mt-2 font-jakarta text-2xl sm:text-3xl font-bold text-slate-900">
              {appData.position || "Poste non précisé"}
            </h1>
            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{appData.company || "Entreprise non précisée"}</span>
              {appData.location && <span className="flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{appData.location}</span>}
              <span>· {new Date(appData.created_at).toLocaleDateString("fr-FR")}</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select value={appData.status} onValueChange={setStatus} disabled={savingStatus}>
              <SelectTrigger className="w-[200px] bg-white rounded-full h-10" data-testid="detail-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Analysis */}
        <Card className="mt-6 p-6 sm:p-8 rounded-2xl border-slate-200 bg-white">
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1 flex flex-col items-center text-center">
              <ScoreRing value={a?.score || 0} />
              <p className="mt-4 text-sm text-slate-600 leading-relaxed max-w-xs">
                {a?.summary || "Analyse indicative de la compatibilité."}
              </p>
              <p className="mt-4 text-xs text-slate-400 leading-relaxed max-w-xs">
                Score indicatif basé uniquement sur les informations fournies. Il ne
                garantit pas l'obtention d'un entretien ou d'un emploi.
              </p>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center gap-2 text-emerald-700 font-semibold">
                  <CheckCircle2 className="h-4 w-4" /> Points forts
                </div>
                <ul className="mt-3 space-y-2" data-testid="analysis-strengths">
                  {(a?.strengths || []).map((s, i) => (
                    <li key={i} className="text-sm text-slate-700 flex gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />{s}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="flex items-center gap-2 text-amber-700 font-semibold">
                  <TrendingUp className="h-4 w-4" /> À améliorer
                </div>
                <ul className="mt-3 space-y-2" data-testid="analysis-improvements">
                  {(a?.improvements || []).map((s, i) => (
                    <li key={i} className="text-sm text-slate-700 flex gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 shrink-0" />{s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center gap-2 text-blue-700 font-semibold">
                  <Sparkles className="h-4 w-4" /> Mots-clés importants
                </div>
                <div className="mt-3 flex flex-wrap gap-2" data-testid="analysis-keywords">
                  {(a?.keywords || []).map((k, i) => (
                    <Badge key={i} className="bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-50 font-medium">{k}</Badge>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center gap-2 text-slate-800 font-semibold">
                  <Lightbulb className="h-4 w-4" /> Recommandations
                </div>
                <ul className="mt-3 space-y-2" data-testid="analysis-recommendations">
                  {(a?.recommendations || []).map((s, i) => (
                    <li key={i} className="text-sm text-slate-700 flex gap-2">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />{s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </Card>

        {/* Generation section */}
        <div className="mt-8 flex items-center justify-between">
          <div>
            <h2 className="font-jakarta text-xl font-bold text-slate-900">Documents de candidature</h2>
            <p className="text-sm text-slate-500 mt-1">CV adapté, lettre, message LinkedIn, email et prep entretien.</p>
          </div>
          {!isPro ? (
            <div className="flex flex-col items-end gap-2">
              <Button onClick={doUpgrade} disabled={upgrading} className="bg-blue-600 hover:bg-blue-700 rounded-full px-5 h-11" data-testid="detail-upgrade-btn">
                {upgrading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Lock className="h-4 w-4 mr-1.5" />}
                Passer Pro à 5,39€
              </Button>
              {checkoutUrl && (
                <a
                  href={checkoutUrl}
                  className="text-xs underline text-blue-700 hover:text-blue-800 font-medium"
                  data-testid="payment-fallback-link-top"
                >
                  Redirection bloquée ? Ouvrir Stripe Checkout →
                </a>
              )}
            </div>
          ) : (
            <Button onClick={generate} disabled={generating} className="bg-blue-600 hover:bg-blue-700 rounded-full px-5 h-11" data-testid="detail-generate-btn">
              {generating ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Wand2 className="h-4 w-4 mr-1.5" />}
              {assets ? "Régénérer" : "Générer les documents"}
            </Button>
          )}
        </div>

        {!assets ? (
          <Card className="mt-4 p-8 rounded-2xl border-dashed border-slate-300 bg-white text-center">
            {isPro ? (
              <>
                <p className="text-slate-600">Clique sur "Générer les documents" pour créer ton CV adapté, ta lettre et plus.</p>
              </>
            ) : (
              <div className="max-w-md mx-auto">
                <Badge className="bg-blue-50 text-blue-700 border-blue-100 hover:bg-blue-50">Offre de lancement · -40%</Badge>
                <h3 className="mt-3 font-jakarta text-xl font-semibold text-slate-900">Débloque la génération complète</h3>
                <p className="mt-2 text-sm text-slate-600">CV adapté, lettre, message LinkedIn, email recruteur et préparation à l'entretien.</p>
                <div className="mt-4 flex items-baseline gap-2 justify-center">
                  <div className="font-jakarta text-3xl font-extrabold text-blue-600">5,39€</div>
                  <div className="text-slate-400 line-through">8,99€</div>
                </div>
                <Button onClick={doUpgrade} disabled={upgrading} className="mt-4 bg-blue-600 hover:bg-blue-700 rounded-full" data-testid="detail-upgrade-cta">
                  {upgrading ? "…" : "Passer Pro maintenant"}
                </Button>
                {checkoutError && (
                  <div
                    className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
                    data-testid="checkout-error"
                  >
                    {checkoutError}
                  </div>
                )}
                {checkoutUrl && (
                  <div className="mt-4 text-sm">
                    <a
                      href={checkoutUrl}
                      className="inline-flex items-center gap-1 underline text-blue-700 hover:text-blue-800 font-semibold"
                      data-testid="payment-fallback-link"
                    >
                      Continuer vers le paiement Stripe →
                    </a>
                    <p className="mt-1 text-xs text-slate-500">
                      Si la page ne s'ouvre pas automatiquement, clique sur le lien ci-dessus.
                    </p>
                  </div>
                )}
              </div>
            )}
          </Card>
        ) : (
          <Card className="mt-4 p-6 sm:p-8 rounded-2xl border-slate-200 bg-white">
            <Tabs defaultValue="cv">
              <TabsList className="bg-slate-100 rounded-full p-1 flex flex-wrap h-auto gap-1">
                <TabsTrigger value="cv" data-testid="tab-cv" className="rounded-full data-[state=active]:bg-white">CV adapté</TabsTrigger>
                <TabsTrigger value="letter" data-testid="tab-letter" className="rounded-full data-[state=active]:bg-white">Lettre</TabsTrigger>
                <TabsTrigger value="linkedin" data-testid="tab-linkedin" className="rounded-full data-[state=active]:bg-white">LinkedIn</TabsTrigger>
                <TabsTrigger value="email" data-testid="tab-email" className="rounded-full data-[state=active]:bg-white">Email</TabsTrigger>
                <TabsTrigger value="interview" data-testid="tab-interview" className="rounded-full data-[state=active]:bg-white">Entretien</TabsTrigger>
              </TabsList>

              <TabsContent value="cv" className="mt-6">
                <AssetEditor
                  label="CV réorganisé pour cette offre (modifiable)"
                  value={editable.cv_adapted || ""}
                  onChange={(v) => setEditable({ ...editable, cv_adapted: v })}
                  onSave={() => saveAsset("cv_adapted")}
                  onCopy={() => copy(editable.cv_adapted)}
                  rows={16}
                  testid="asset-cv"
                />
              </TabsContent>

              <TabsContent value="letter" className="mt-6">
                <AssetEditor
                  label="Lettre de motivation (modifiable)"
                  value={editable.cover_letter || ""}
                  onChange={(v) => setEditable({ ...editable, cover_letter: v })}
                  onSave={() => saveAsset("cover_letter")}
                  onCopy={() => copy(editable.cover_letter)}
                  rows={12}
                  testid="asset-letter"
                />
              </TabsContent>

              <TabsContent value="linkedin" className="mt-6">
                <AssetEditor
                  label="Message LinkedIn (modifiable, max ~300 caractères)"
                  value={editable.linkedin_message || ""}
                  onChange={(v) => setEditable({ ...editable, linkedin_message: v })}
                  onSave={() => saveAsset("linkedin_message")}
                  onCopy={() => copy(editable.linkedin_message)}
                  rows={5}
                  testid="asset-linkedin"
                />
              </TabsContent>

              <TabsContent value="email" className="mt-6 space-y-4">
                <div>
                  <label className="text-sm text-slate-700 font-medium">Objet</label>
                  <Input
                    data-testid="asset-email-subject"
                    value={editable.recruiter_email_subject || ""}
                    onChange={(e) => setEditable({ ...editable, recruiter_email_subject: e.target.value })}
                    className="mt-1.5"
                  />
                  <div className="mt-1.5 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => saveAsset("recruiter_email_subject")}>Enregistrer</Button>
                    <Button size="sm" variant="ghost" onClick={() => copy(editable.recruiter_email_subject)}><Copy className="h-3.5 w-3.5 mr-1" /> Copier</Button>
                  </div>
                </div>
                <AssetEditor
                  label="Corps de l'email"
                  value={editable.recruiter_email_body || ""}
                  onChange={(v) => setEditable({ ...editable, recruiter_email_body: v })}
                  onSave={() => saveAsset("recruiter_email_body")}
                  onCopy={() => copy(editable.recruiter_email_body)}
                  rows={10}
                  testid="asset-email-body"
                />
              </TabsContent>

              <TabsContent value="interview" className="mt-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-jakarta font-semibold text-slate-900">Questions probables</h4>
                    <ol className="mt-3 space-y-2 list-decimal list-inside text-sm text-slate-700" data-testid="interview-questions">
                      {(assets.interview_questions || []).map((q, i) => <li key={i}>{q}</li>)}
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-jakarta font-semibold text-slate-900">Conseils pour bien répondre</h4>
                    <ul className="mt-3 space-y-2 text-sm text-slate-700" data-testid="interview-advice">
                      {(assets.interview_advice || []).map((c, i) => (
                        <li key={i} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />{c}</li>
                      ))}
                    </ul>
                  </div>
                  <div className="md:col-span-2">
                    <h4 className="font-jakarta font-semibold text-slate-900">Points du CV à mettre en avant</h4>
                    <ul className="mt-3 space-y-2 text-sm text-slate-700" data-testid="interview-highlights">
                      {(assets.interview_highlights || []).map((h, i) => (
                        <li key={i} className="flex gap-2"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />{h}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </Card>
        )}
      </main>
    </div>
  );
}

function AssetEditor({ label, value, onChange, onSave, onCopy, rows = 10, testid }) {
  return (
    <div>
      <label className="text-sm text-slate-700 font-medium">{label}</label>
      <Textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        className="mt-1.5 text-sm leading-relaxed"
      />
      <div className="mt-3 flex gap-2">
        <Button size="sm" variant="outline" onClick={onSave} className="rounded-full" data-testid={`${testid}-save`}>Enregistrer</Button>
        <Button size="sm" variant="ghost" onClick={onCopy} className="rounded-full" data-testid={`${testid}-copy`}><Copy className="h-3.5 w-3.5 mr-1" /> Copier</Button>
      </div>
    </div>
  );
}
